<template>
    <div class="v-atlas-raumplanung-toc-filter-tree">

        <!-- Pinned filters as removable chips -->
        <!--
        <h3>Aktive Filter:</h3>
        <div v-if="pinnedChips.length" class="v-atlas-raumplanung-toc-filter-tree__pinned">
            <span v-for="chip in pinnedChips" :key="chip.id" class="v-atlas-raumplanung-toc-filter-tree__chip">
                <span class="v-atlas-raumplanung-toc-filter-tree__chip-label">{{ chip.label }}</span>
                <button class="v-atlas-raumplanung-toc-filter-tree__chip-remove"
                    :data-tooltip="`Filter lösen: ${chip.label}`" @click="unpinChip(chip.id)">
                    ×
                </button>
            </span>
        </div>
-->
        <!-- "All indicators" option -->

        <h3>Filteroptionen:</h3>
        <button class="v-atlas-raumplanung-toc-filter-tree__all"
            :class="{ 'v-atlas-raumplanung-toc-filter-tree__node--selected': !hasAnyFilter }" @click="selectAll">
            <span class="v-atlas-raumplanung-toc-filter-tree__expand-icon" />
            <span class="v-atlas-raumplanung-toc-filter-tree__node-label">
                Alle Indikatoren (alle Filter zurücksetzen)
            </span>
        </button>

        <!-- Category tree -->

        <ul v-if="decoratedTree.length" class="v-atlas-raumplanung-toc-filter-tree__tree">
            <li v-for="d in decoratedTree" :key="d.node.tag">
                <TocFilterTreeNode :node="d.node" :selected="d.selected" :active-path="d.activePath" :pinned="d.pinned"
                    :allow-pinning="d.allowPinning" :children-view="d.children" :text-query="textQuery"
                    :is-text-node="d.isTextNode" @select="selectTag" @pin="pinTag" @unpin="unpinTag"
                    @update:text="textQuery = $event" />
            </li>
        </ul>

        <div v-else class="v-atlas-raumplanung-toc-filter-tree__empty">
            Keine Kategorien verfügbar.
        </div>

    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
    RaumplanungTocFilterTreeNode,
    type SpatialUnitType,
} from 'atlas-shared';
import TocFilterTreeNode from './TocFilterTreeNode.vue';
import type { IndicatorFilterRegistry } from './filters/filterRegistry';
import { ADMINISTRATIVE_LEVELS } from './filters/filterRegistry';
import { IsFavoriteFilter } from './filters/IsFavoriteFilter';
import { TextFilter } from './filters/TextFilter';
import { tagFilterId } from './filters/HasTagFilter';
import { regionalDepthFilterId } from './filters/RegionalDepthFilter';
import { useTreeFilterRegistration } from './filters/useTreeFilterRegistration';

const props = defineProps<{
    tree: RaumplanungTocFilterTreeNode[];
    registry: IndicatorFilterRegistry;
}>();

const tree = computed<RaumplanungTocFilterTreeNode[]>(() => props.tree ?? []);

/** Reserved tag for the synthetic "Favoriten" top-level node. */
const FAVORITES_TAG = '__favorites'

/** The id of the favorites filter (see IsFavoriteFilter). */
const FAVORITES_FILTER_ID = 'isFavorite'

/** Reserved tag for the synthetic "Textsuche" top-level node. */
const TEXT_TAG = '__text'

/** The id of the text filter (see TextFilter). */
const TEXT_FILTER_ID = 'text'

/** Reserved tag for the synthetic "Administrative Ebene" top-level node. */
const REGIONAL_DEPTH_TAG = '__regionalDepth'

/** Reserved tag for the synthetic "Thema" top-level node. */
const THEMA_TAG = '__thema'

/**
 * Synthetic top-level node representing the favorites filter. It is a leaf
 * (no children) so it renders like other leaves, including its pin button.
 */
const favoritesNode: RaumplanungTocFilterTreeNode = {
    label: 'Favoriten',
    tag: FAVORITES_TAG,
    children: [],
}

/**
 * Synthetic top-level node representing the text search filter. It is a leaf
 * (no children) so it renders like other leaves, including its pin button.
 */
const textNode: RaumplanungTocFilterTreeNode = {
    label: 'Textsuche',
    tag: TEXT_TAG,
    children: [],
}

/**
 * Synthetic top-level node representing the administrative-level filter. It is
 * a category node whose children are the individual administrative levels
 * (Gemeinde, Kreis, …), each rendered as a leaf with its own
 * {@link RegionalDepthFilter}.
 */
const regionalDepthNode: RaumplanungTocFilterTreeNode = {
    label: 'Administrative Ebene',
    tag: REGIONAL_DEPTH_TAG,
    exclusiveGroup: 'administrativeLevel',
    children: ADMINISTRATIVE_LEVELS.map(level => ({
        label: level,
        tag: `${REGIONAL_DEPTH_TAG}:${level}`,
        children: [],
    })),
}

/**
 * Synthetic top-level node wrapping all category-tree branches that come from
 * the server response under a single "Thema" node.
 */
const themaNode = computed<RaumplanungTocFilterTreeNode>(() => ({
    label: 'Thema',
    tag: THEMA_TAG,
    exclusiveGroup: 'thema',
    children: tree.value,
}))

/**
 * The tree used for tag-filter registration: just the synthetic "Thema" wrapper
 * around the server category tree. This ensures the "Thema" node's
 * `exclusiveGroup` is inherited down to every category leaf filter, while the
 * synthetic favorites/text/administrative-level nodes (which use their own
 * pre-registered filters) are excluded.
 */
const themaRegistrationTree = computed<RaumplanungTocFilterTreeNode[]>(() => [themaNode.value])

/**
 * The effective tree: the synthetic favorites, text and administrative-level
 * nodes prepended to the "Thema" node that wraps the category tree from the
 * server config. Added at runtime on component initialization.
 */
const treeWithSynthetic = computed<RaumplanungTocFilterTreeNode[]>(() => [
    textNode,
    favoritesNode,
    regionalDepthNode,
    themaNode.value,
]);

const isFavFilter = computed(() => props.registry.getFilter('isFavorite') as IsFavoriteFilter | null);
const textFilter = computed(() => props.registry.getFilter('text') as TextFilter | null);

/**
 * Single reactive source of the registry's selection state (focus + pins),
 * reused by the highlighting logic. Each tree leaf is associated with a filter
 * id; a node's presentation state is derived purely from whether its associated
 * filter is active/pinned/selected — independent of any tag-string semantics.
 */
const activeFilterIds = computed(() => new Set(props.registry.getActiveFilterIds()));
const pinnedFilterIds = computed(() => new Set(props.registry.getPinnedIds()));
const focusedFilterId = computed<string | null>(() => props.registry.getFocusedFilter()?.id ?? null);

/**
 * The currently pinned filters, rendered as removable chips at the top of the
 * panel. Each chip shows the filter's label and an "×" button to unpin it.
 *
 * The chip label is enriched with context:
 * - the text filter shows its current query value,
 * - administrative-level filters are prefixed with "Administrative Ebene: ",
 * - Thema (category tag) filters are prefixed with "Thema: ".
 */
const pinnedChips = computed<Array<{ id: string; label: string }>>(() =>
    props.registry.getPinnedIds()
        .map(id => props.registry.getFilter(id))
        .filter((f): f is NonNullable<typeof f> => f !== null)
        .map(f => ({ id: f.id, label: chipLabelFor(f) })),
);

/** Builds the enriched chip label for a pinned filter. */
function chipLabelFor(f: NonNullable<ReturnType<IndicatorFilterRegistry['getFilter']>>): string {
    if (f.id === TEXT_FILTER_ID) {
        const query = (f as TextFilter).query.value.trim()
        return query ? `Textsuche: ${query}` : f.label
    }
    if (f.id.startsWith('regionalDepth:')) {
        return `Administrative Ebene: ${f.label}`
    }
    if (f.id.startsWith('tag:')) {
        return `Thema: ${f.label}`
    }
    return f.label
}

/** Whether the favorites filter is active. */
const isFavActive = computed<boolean>(() => isFavFilter.value?.active.value ?? false);

/** Whether any filter is currently restricting the list (focus, pin, text or favorite). */
const hasAnyFilter = computed<boolean>(() =>
    activeFilterIds.value.size > 0 ||
    isFavActive.value ||
    (textFilter.value?.query.value.trim().length ?? 0) > 0,
);

/** Current text filter query (writable, feeds the smart text input). */
const textQuery = computed<string>({
    get: () => textFilter.value?.query.value ?? "",
    set: (value: string) => {
        props.registry.applyConfig(textFilter.value?.id ?? "text", value)
        // The text filter is always pinned while there is a query, and always
        // unpinned when the field is empty. Clicking the label has no effect.
        if (value.trim().length > 0) {
            props.registry.pin(TEXT_FILTER_ID)
        } else {
            props.registry.unpin(TEXT_FILTER_ID)
        }
    },
});

//#region Decorated tree (filter-activity based highlighting)

/**
 * A tree node annotated with the presentation state derived from its associated
 * filter's activity in the registry.
 *
 * - `filterId`: the id of the filter associated with this node (leaf filters
 *   only). Resolved via the registry (created per leaf, id `tag:<path>` today),
 *   so the check is purely "is this filter active/pinned/selected".
 * - `selected`: the node's own associated filter is the focused one.
 * - `activePath`: this node's own filter is active, or any descendant's leaf
 *   filter is active — used to bold the whole path down to an active filter.
 * - `pinned`: the node's own associated filter is pinned.
 * - `allowPinning`: whether pinning is enabled for this node. `false` when the
 *   node itself or any ancestor has `allowPinning` set to `true` (pinning is
 *   then disabled for all filter nodes in the sub-branch).
 */
interface DecoratedTreeNode {
    node: RaumplanungTocFilterTreeNode;
    filterId: string | null;
    selected: boolean;
    activePath: boolean;
    pinned: boolean;
    allowPinning: boolean;
    isTextNode: boolean;
    children: DecoratedTreeNode[];
}

/**
 * Resolves the id of the filter associated with a leaf node. Today every leaf
 * (identified by its tag path) gets a per-leaf tag filter whose id is produced
 * by {@link tagFilterId}. This helper centralises the association so future
 * filter types on the tree (selected by `node`, not by its `tag`) can be
 * resolved differently.
 */
function filterIdForNode(node: RaumplanungTocFilterTreeNode): string | null {
    return node.children?.length ? null : filterIdForTag(node.tag)
}

/**
 * Resolves the filter id associated with a tree tag. The synthetic favorites
 * node maps to the isFavorite filter; the administrative-level leaves map to
 * their RegionalDepthFilter; every other leaf maps to its tag filter.
 */
function filterIdForTag(tag: string): string {
    if (tag === FAVORITES_TAG) return FAVORITES_FILTER_ID
    if (tag === TEXT_TAG) return TEXT_FILTER_ID
    if (tag.startsWith(`${REGIONAL_DEPTH_TAG}:`)) {
        return regionalDepthFilterId(tag.slice(REGIONAL_DEPTH_TAG.length + 1) as SpatialUnitType)
    }
    return tagFilterId(tag)
}

function decorateNode(node: RaumplanungTocFilterTreeNode, allowPinning = true): DecoratedTreeNode {
    const filterId = filterIdForNode(node)
    const children = (node.children ?? []).map(child => decorateNode(child, allowPinning && node.allowPinning !== false))
    const ownActive = filterId !== null && activeFilterIds.value.has(filterId)
    const childActive = children.some(c => c.activePath)
    return {
        node,
        filterId,
        selected: filterId !== null && focusedFilterId.value === filterId,
        pinned: filterId !== null && pinnedFilterIds.value.has(filterId),
        allowPinning,
        activePath: ownActive || childActive,
        isTextNode: node.tag === TEXT_TAG,
        children,
    }
}

/** The decorated tree, re-computed reactively when the registry state changes. */
const decoratedTree = computed<DecoratedTreeNode[]>(() =>
    treeWithSynthetic.value.map(node => decorateNode(node)),
);

//#endregion Decorated tree

// Register one tag filter per tree leaf and keep it in sync (handles reload/unmount).
// Uses the synthetic "Thema" wrapper so its exclusiveGroup is inherited down to
// all category leaf filters. (The administrative-level and favorites/text leaves
// are handled by their own pre-registered filters, not tag filters.)
useTreeFilterRegistration(props.registry, themaRegistrationTree)

function selectTag(tag: string | null) {
    if (tag === null) {
        props.registry.setActiveFilter(null)
        return
    }
    const id = filterIdForTag(tag)
    // The text filter must never be cleared by clicking its node: the query is
    // the user's input and should persist. Clicking the text node only focuses
    // the filter (shows the input) — it never resets the query.
    if (id === TEXT_FILTER_ID) {
        props.registry.activate(id)
        return
    }
    // The favorites filter carries its own active flag, so it is activated via
    // applyConfig; other leaves are activated purely by focusing their tag filter.
    if (id === FAVORITES_FILTER_ID) {
        props.registry.applyConfig(id, true)
    } else {
        props.registry.activate(id)
    }
}

function pinTag(tag: string) {
    const id = filterIdForTag(tag)
    // Pinning activates the filter (it becomes part of the active set) but does
    // NOT set the focus: the filter stays active purely through its pin.
    props.registry.pin(id)
    // The favorites filter needs its own active flag set to actually filter.
    if (id === FAVORITES_FILTER_ID) {
        props.registry.getFilter(id)?.setConfig(true)
    }
}

function unpinTag(tag: string) {
    props.registry.unpin(filterIdForTag(tag))
}

/** Unpins a filter by its id (used by the pinned-filter chips). */
function unpinChip(id: string) {
    props.registry.unpin(id)
}

function selectAll() {
    // "Alle Indikatoren" removes all filters – focus, pins, text and favorites.
    props.registry.clear()
    // The text filter's reset() is intentionally a no-op (the query is the
    // user's input and normally persists), but "alle Filter zurücksetzen"
    // should also clear the text search field.
    textFilter.value?.setConfig("")
}
</script>